import React from 'react';
import { 
  Package, 
  Tag, 
  Star, 
  StarOff, 
  Package2, 
  Image as ImageIcon
} from 'lucide-react';
import { ProductResource } from '../types';
import { getProductTypeLabel } from '../utils/helpers';

interface ProductCardProps {
  product: ProductResource;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      {/* Product Image */}
      <div className="aspect-square bg-gray-100 flex items-center justify-center">
        {product.imageFile ? (
          <img 
            src={product.imageFile} 
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center text-gray-400">
            <ImageIcon className="h-12 w-12 mb-2" />
            <span className="text-xs">No Image</span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate mb-1">
              {product.name}
            </h3>
            {product.code && (
              <p className="text-sm text-gray-500 mb-2">{product.code}</p>
            )}
          </div>
          <div className="flex flex-col items-end space-y-1 ml-2">
            {/* Status Badges */}
            <div className="flex items-center space-x-1">
              {product.isActive ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Inactive
                </span>
              )}
            </div>
            <div className="flex items-center space-x-1">
              {product.isFavorite ? (
                <Star className="h-4 w-4 text-yellow-500" />
              ) : (
                <StarOff className="h-4 w-4 text-gray-400" />
              )}
              {product.isBundle && (
                <Package2 className="h-4 w-4 text-blue-500" />
              )}
            </div>
          </div>
        </div>

        {/* Category */}
        <div className="flex items-center mb-3">
          <Tag className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-sm text-gray-600">
            {product.category ? product.category.name : 'No Category'}
          </span>
        </div>

        {/* Description */}
        {product.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {product.description}
          </p>
        )}

        {/* Financial Info */}
        <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
          <div>
            <span className="text-gray-500">COGS:</span>
            <span className="ml-2 font-medium text-gray-900">{formatCurrency(product.cogs)}</span>
          </div>
          <div>
            <span className="text-gray-500">Price:</span>
            <span className="ml-2 font-medium text-green-600">{formatCurrency(product.sellingPrice)}</span>
          </div>
        </div>

        {/* Stock Info */}
        <div className="flex items-center justify-between mb-3 text-sm">
          <div className="flex items-center">
            <Package className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-gray-500">Stock:</span>
            <span className={`ml-2 font-medium ${
              product.currentQty > 0 
                ? 'text-green-600' 
                : product.currentQty === 0 
                  ? 'text-yellow-600' 
                  : 'text-red-600'
            }`}>
              {product.currentQty.toLocaleString()}
              {product.currentQty < 0 && <span className="text-xs ml-1">(Oversold)</span>}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {product.unit && (
              <span className="text-gray-500">{product.unit}</span>
            )}
            {/* Stock Status Badge */}
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              product.currentQty > 10 
                ? 'bg-green-100 text-green-800' 
                : product.currentQty > 0 
                  ? 'bg-yellow-100 text-yellow-800'
                  : product.currentQty === 0
                    ? 'bg-red-100 text-red-800'
                    : 'bg-red-200 text-red-900'
            }`}>
              {product.currentQty > 10 
                ? 'In Stock' 
                : product.currentQty > 0 
                  ? 'Low Stock'
                  : product.currentQty === 0
                    ? 'Out of Stock'
                    : 'Oversold'
              }
            </span>
          </div>
        </div>

        {/* Additional Info */}
        <div className="space-y-2 text-xs text-gray-500">
          {product.barcodeCode && (
            <div className="flex items-center">
              <span className="mr-2">Barcode:</span>
              <span className="font-mono bg-gray-100 px-2 py-1 rounded">{product.barcodeCode}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span>Type: {getProductTypeLabel(product.type)}</span>
            <span>Created: {formatDate(product.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
